import {
  CaretRightOutlined,
  LeftOutlined,
  MailOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { UserRole } from '@cc/schema';
import { useCustom, useTranslate } from '@refinedev/core';
import { App as AntdApp, Avatar, Button, ConfigProvider, Tag, Tooltip, Typography } from 'antd';
import { createStyles } from 'antd-style';
import { differenceInMinutes, format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClientSessionTimerContext } from '../../App';
import { sessionFinishFailure } from '../../hooks/useClientSessionTimer';
import { ReviewSessionControl } from '../../components/layout/ReviewSessionControl';
import { useClientRoster } from '../../hooks/client-options';
import { AssignHomeworkDrawer } from '../homework/AssignHomeworkDrawer';
import { isNotConnected } from '../homework/model';
import { useRefreshClientPage } from './refreshClientPage';

/**
 * E57 · ClientHeader — "The client header on EVERY section of the client page … initials avatar, name, ID, status chips
 * (App client or not, access level), the last session, and the actions Start Client Session · Start Review Session (End
 * while one is open) · Assign homework · Message." (cc-practice, "Client Management — Client page frame &
 * navigation (portal, E57)", screen e57e0001-…-015).
 *
 * D-nav-fixes-2026-10-19 (founder Q-nav-2 A): ONE line — ‹ Clients · avatar · name · ID · chips · last session, then the
 * five actions as compact 32 px buttons. The back-arrow row and the page card are gone (show.tsx); the line is sticky with
 * the navigation rows under it. When the line runs out of room it gives way in a fixed order — Message, then Assign homework
 * become icon buttons with a tooltip (still one click each), then the
 * last-session text hides, then the ID text (both stay in the name's tooltip), and only then the chips shrink to an ellipsis
 * (full text in a tooltip) and, last, the name itself — one line, nothing ever clipped without an ellipsis or overlapped
 * (the sweep in E57-client-page.spec.ts, 1100–1700 px, EN and RU). Measured on the real DOM (`fitHeader`), never guessed from the
 * window width: the global menu, the language and the session state all change what fits.
 *
 * Behavior lines implemented here:
 *   · nav: ‹ Clients returns to the clients list (clientPage.header.back);
 *   · nav: Assign homework opens the E34 assign drawer with this client preselected (clientPage.header.assignHomework);
 *   · nav: Message opens this client's messages (clientPage.header.message);
 *   · (N-2, founder Q-e57h-1 A, 2026-10-22: no Safety plan button here — the "Safety" tab is the one way in);
 *   · gesture: Start / Finish Client Session toggle THE shared session timer (components.header.startSession) — pinned to
 *     the client it was started for (Q-e57g1-2 A): on another client's page the button still finishes THAT session, and
 *     says whose it is;
 *   · gesture: Start / End Review Session — E04's control, the same commands, the elapsed time while open
 *     (clientActivity… / components.header.*ReviewSession);
 *   · format: last session as "Fri 10 Oct, 50 min" (clientPage.header.lastSession);
 *   · format: one line; Message → Assign homework turn into icon buttons with tooltips when tight (Q-nav-2 A);
 *   · a11y: the header is a landmark naming the client (clientPage.header.aria).
 */
const useStyles = createStyles(({ token, css }) => ({
  line: css`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 10px;
    min-height: 52px;
    padding: 6px 0;
    overflow: hidden;
  `,
  crumb: css`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: none;
    padding-right: 10px;
    border-right: 1px solid ${token.colorBorderSecondary};
    color: ${token.colorPrimary};
    font-size: 14px;
    white-space: nowrap;
  `,
  who: css`
    display: flex;
    align-items: center;
    gap: 10px;
    flex: none;
    white-space: nowrap;
  `,
  // still too wide with every step taken: the chips shrink to an ellipsis (full text in a tooltip), the name last
  whoSqueezed: css`
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    .cc-meta {
      flex: 0 1 auto;
      min-width: 112px;
    }
    .ant-tag {
      display: block;
      flex: 0 100 auto;
      min-width: 52px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .ant-typography.ant-typography.cc-name {
      flex: 0 1 auto;
      min-width: 72px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `,
  name: css`
    &.ant-typography {
      flex: none;
      margin: 0 !important;
      font-size: 19px;
      line-height: 1.25;
      white-space: nowrap;
    }
  `,
  meta: css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    overflow: hidden;
    font-size: 13.5px;
    white-space: nowrap;
    .ant-tag {
      margin: 0;
      font-size: 12px;
      line-height: 19px;
      padding: 0 7px;
    }
  `,
  elsewhere: css`
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
  `,
  actions: css`
    margin-left: auto;
    display: flex;
    gap: 6px;
    flex: none;
    .ant-btn {
      gap: 5px;
      /* N-2: antd animates every property of a button (\`transition: all\`), so when the fit swaps a label for its icon the width
         glides over 200 ms and the fit's own measuring (and a second look a moment later) reads a width that is on its way:
         the line then settles differently from run to run. Only the colors animate; size changes are immediate. */
      transition: color 0.2s, background-color 0.2s, border-color 0.2s, box-shadow 0.2s;
    }
  `,
  iconOnly: css`
    &.ant-btn {
      width: 32px;
      padding-inline: 0;
    }
    .cc-actlbl {
      display: none;
    }
  `,
}));

/**
 * The sticky block of the client page: the one-line header and the two navigation rows, on the page's own background,
 * reaching over the layout's side padding so the section scrolling under it never shows at its edges.
 */
const useStickyStyles = createStyles(({ token, css }) => ({
  top: css`
    position: sticky;
    top: 0;
    z-index: 20;
    background: ${token.colorBgLayout};
    @media (min-width: 576px) {
      margin: -12px -24px 0;
      padding: 0 24px;
    }
  `,
}));

export const ClientStickyTop: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { styles } = useStickyStyles();
  return (
    <div className={styles.top} data-testid="cc-client-sticky-top">
      {children}
    </div>
  );
};

/** The buttons that turn into icons, in the order they give way (Q-nav-2 A), then the last-session text. */
const FIT_STEPS = ['message', 'assign'] as const;
type FitStep = (typeof FIT_STEPS)[number];

/**
 * Priority fit on the live DOM: show every label, then — while the line overflows — make the next button icon-only,
 * then hide the last-session text; finally give labels back, highest priority first, wherever they fit again. Only
 * classes and inline display change here (attributes), so the MutationObserver that calls it never sees its own work;
 * React then renders the same state (`Fit`), so the two never disagree.
 */
type Fit = { icons: readonly FitStep[]; lastSessionHidden: boolean; idHidden: boolean; crumbIcon: boolean; avatarHidden: boolean; squeezed: boolean };
const NO_FIT: Fit = { icons: [], lastSessionHidden: false, idHidden: false, crumbIcon: false, avatarHidden: false, squeezed: false };

const fitHeader = (el: HTMLElement, iconOnlyClass: string, squeezedClass: string): Fit => {
  // to the sub-pixel: scrollWidth is rounded, and a button 1.5 px past the edge is a clipped button
  const overflows = () => {
    const right = el.getBoundingClientRect().right;
    return el.scrollWidth > el.clientWidth || Array.from(el.children).some((c) => c.getBoundingClientRect().right > right + 0.5);
  };
  const btn = (k: FitStep) => el.querySelector<HTMLElement>(`[data-fit-step="${k}"]`);
  const ls = el.querySelector<HTMLElement>('[data-fit-ls]');
  const who = el.querySelector<HTMLElement>('[data-fit-who]');
  const idEl = el.querySelector<HTMLElement>('[data-fit-id]');
  const crumbLabel = el.querySelector<HTMLElement>('[data-fit-crumb-label]');
  const avatar = el.querySelector<HTMLElement>('[data-fit-avatar]');
  // measure at full width: a squeezed name block would hide the overflow it exists to absorb (and the two would loop)
  who?.classList.remove(squeezedClass);
  FIT_STEPS.forEach((k) => btn(k)?.classList.remove(iconOnlyClass));
  if (ls) ls.style.display = '';
  if (idEl) idEl.style.display = '';
  if (crumbLabel) crumbLabel.style.display = '';
  if (avatar) avatar.style.display = '';
  for (const k of FIT_STEPS) {
    if (!overflows()) break;
    btn(k)?.classList.add(iconOnlyClass);
  }
  let lastSessionHidden = false;
  if (overflows() && ls) {
    ls.style.display = 'none';
    lastSessionHidden = true;
  }
  let idHidden = false;
  if (lastSessionHidden && overflows() && idEl) {
    idEl.style.display = 'none';
    idHidden = true;
  }
  let crumbIcon = false;
  let avatarHidden = false;
  if (idHidden && overflows() && crumbLabel) {
    crumbLabel.style.display = 'none';
    crumbIcon = true;
  }
  if (crumbIcon && overflows() && avatar) {
    avatar.style.display = 'none';
    avatarHidden = true;
  }
  if (!lastSessionHidden) {
    // give labels back where they fit again (highest priority first: Assign homework, Message)
    for (const k of [...FIT_STEPS].reverse()) {
      const b = btn(k);
      if (!b || !b.classList.contains(iconOnlyClass)) continue;
      b.classList.remove(iconOnlyClass);
      if (overflows()) b.classList.add(iconOnlyClass);
    }
  }
  const squeezed = overflows();
  if (squeezed) who?.classList.add(squeezedClass);
  // what an ellipsis cut says so (its full text is the tooltip's) — an attribute only, the spec reads it
  el.querySelectorAll<HTMLElement>('[data-fit-trunc]').forEach((n) =>
    n.setAttribute('data-truncated', n.scrollWidth > n.clientWidth + 0.5 ? 'true' : 'false'),
  );
  return {
    icons: FIT_STEPS.filter((k) => btn(k)?.classList.contains(iconOnlyClass)),
    lastSessionHidden,
    idHidden,
    crumbIcon,
    avatarHidden,
    squeezed,
  };
};

const sameFit = (a: Fit, b: Fit) =>
  a.lastSessionHidden === b.lastSessionHidden && a.idHidden === b.idHidden && a.crumbIcon === b.crumbIcon && a.avatarHidden === b.avatarHidden && a.squeezed === b.squeezed && a.icons.join() === b.icons.join();

type HeaderData = {
  subject: string;
  firstName?: string | null;
  lastName?: string | null;
  role?: number | null;
  accessLevel?: string | null;
  appClient: boolean;
  lastSession?: { start: string; end?: string | null } | null;
  reviewSession?: { reviewSessionId: string; startedAt: string } | null;
};

export const CLIENT_HEADER_FIELDS = [
  'subject',
  'firstName',
  'lastName',
  'role',
  'accessLevel',
  'appClient',
  { lastSession: ['start', 'end'] },
  { reviewSession: ['reviewSessionId', 'startedAt'] },
];

export const ClientPageHeader: React.FC<{
  clientId: string;
  locale: 'en' | 'ru';
}> = ({ clientId, locale }) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const sessionTimer = useContext(ClientSessionTimerContext);
  const { clients } = useClientRoster();
  const [assigning, setAssigning] = useState(false);
  const { styles } = useStyles();
  const lineRef = useRef<HTMLElement>(null);
  const [fit, setFit] = useState<Fit>(NO_FIT);

  const { data } = useCustom<HeaderData>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientHeader',
      fields: CLIENT_HEADER_FIELDS,
      variables: { subject: { name: 'subject', type: 'ID!', value: clientId } },
    },
    queryOptions: { enabled: Boolean(clientId) },
  });
  const header = data?.data;

  // the assign drawer's client facts (the hub's own reads: zone, the app's language, can the app show homework)
  const { data: clientData } = useCustom<{ timezone?: string | null; lng?: string | null; homeworkAppReady?: boolean | null }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'client',
      fields: ['_id', 'timezone', 'lng', 'homeworkAppReady'],
      variables: { id: { name: 'id', type: 'ID', value: clientId } },
    },
    queryOptions: { enabled: assigning && Boolean(clientId) },
  });

  // the writes' own question (the Homework tab's): a client who shares nothing with the caller — the drawer says so up
  // front instead of failing on submit (review F8)
  const { data: accessData } = useCustom<{ canAssign: boolean }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientHomeworkAccess',
      fields: ['canAssign'],
      variables: { subject: { name: 'subject', type: 'ID!', value: clientId } },
    },
    queryOptions: { enabled: assigning && Boolean(clientId), retry: false },
  });
  const refreshClientPage = useRefreshClientPage();

  const name =
    [header?.firstName, header?.lastName].filter(Boolean).join(' ') || clientId.replace('users/', '');
  const initials = `${(header?.firstName ?? '').charAt(0)}${(header?.lastName ?? '').charAt(0)}`.toUpperCase() || '·';
  const dateLocale = locale === 'ru' ? ru : enUS;

  const lastSessionText = useMemo(() => {
    const s = header?.lastSession;
    if (!s?.start) return t('clientPage.header.noSession');
    const start = parseISO(s.start);
    const minutes = s.end ? Math.max(0, differenceInMinutes(parseISO(s.end), start)) : null;
    const day = format(start, locale === 'ru' ? 'EEEEEE d MMM' : 'EEE d MMM', { locale: dateLocale });
    return minutes === null
      ? t('clientPage.header.lastSessionNoLength', { date: day })
      : t('clientPage.header.lastSession', { date: day, minutes });
  }, [header?.lastSession, locale, t, dateLocale]);

  const idText = t('clientPage.header.id', { id: clientId.replace('users/', '') });
  const appChipText = header?.appClient === false ? t('clientPage.header.nonAppClient') : t('clientPage.header.appClient');

  const timer = sessionTimer?.timer ?? null;
  const timerHere = timer?.clientId === clientId;
  const timerElsewhere = !!timer && !timerHere;
  const pinnedLabel = (() => {
    if (!timer) return '';
    const c = clients.find((x) => x._id === timer.clientId);
    return c
      ? `${timer.clientId.replace('users/', '')} - ${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()
      : timer.clientId.replace('users/', '');
  })();

  const onSessionClick = async () => {
    if (!timer) {
      sessionTimer.start(clientId);
      return;
    }
    try {
      await sessionTimer.finish();
      refreshClientPage('session'); // the header's last session, the row's count and "new", the digest's cut (review F9)
      message.success(t('pages.sessions.saveSuccess'), 1);
    } catch (error) {
      message.error(sessionFinishFailure(error, t('components.header.sessionSaveFailed')));
    }
  };

  const accessChip =
    header?.role === UserRole.NON_MOBILE || header?.appClient === false
      ? null
      : header?.accessLevel
      ? t(`clients.${header.accessLevel}`)
      : t('clients.free');

  // the priority fit: on the line's own size (the window, the global menu) and on any text change inside it (the name
  // loading, Start ↔ Finish, the review's elapsed time, the language) — never on its own class changes
  const runFit = useCallback(() => {
    const el = lineRef.current;
    if (!el) return;
    const next = fitHeader(el, styles.iconOnly, styles.whoSqueezed);
    setFit((prev) => (sameFit(prev, next) ? prev : next));
  }, [styles.iconOnly, styles.whoSqueezed]);
  useLayoutEffect(() => {
    const el = lineRef.current;
    if (!el) return undefined;
    runFit();
    // the line itself (the window, the menu) AND its parts: a part's size changes with the web font arriving, the review
    // bracket opening, a longer name — none of which is a resize of the line. Deterministic, so the observer settles: the
    // sizes after a run are the sizes it last reported.
    const ro = new ResizeObserver(() => runFit());
    ro.observe(el);
    el.querySelectorAll(':scope > *').forEach((child) => ro.observe(child));
    const mo = new MutationObserver(() => runFit());
    mo.observe(el, { childList: true, characterData: true, subtree: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [runFit]);

  const icon = (k: FitStep) => fit.icons.includes(k);
  /** A compact action that may give way to an icon (Q-nav-2 A): the label in a tooltip then, still one click. */
  const action = (k: FitStep, label: string, iconNode: React.ReactNode, onClick: () => void, testId: string) => (
    <Tooltip title={icon(k) ? label : undefined}>
      <Button
        icon={iconNode}
        onClick={onClick}
        aria-label={label}
        // plain class lists, never `cx`: cx MERGES classes into a new one, which `fitHeader` could not take off again
        className={icon(k) ? styles.iconOnly : undefined}
        data-fit-step={k}
        data-icon-only={icon(k) ? 'true' : 'false'}
        data-testid={testId}
      >
        <span className="cc-actlbl">{label}</span>
      </Button>
    </Tooltip>
  );

  return (
    <header
      ref={lineRef}
      aria-label={t('clientPage.header.aria', { name })}
      data-testid="cc-client-header"
      data-fit={fit.icons.length + (fit.lastSessionHidden ? 1 : 0) + (fit.idHidden ? 1 : 0) + (fit.crumbIcon ? 1 : 0) + (fit.avatarHidden ? 1 : 0)}
      className={styles.line}
    >
      <Link to="/clients" className={styles.crumb} title={t('clientPage.header.back')} aria-label={t('clientPage.header.back')} data-testid="cc-client-header-back">
        <LeftOutlined style={{ fontSize: 12 }} />
        <span data-fit-crumb-label="" style={fit.crumbIcon ? { display: 'none' } : undefined}>
          {t('portalMenu.clients')}
        </span>
      </Link>
      <Avatar size={32} style={{ flex: 'none', fontSize: 12.5, ...(fit.avatarHidden ? { display: 'none' } : {}) }} data-fit-avatar="" data-testid="cc-client-header-avatar">
        {initials}
      </Avatar>
      <div className={fit.squeezed ? `${styles.who} ${styles.whoSqueezed}` : styles.who} data-fit-who="">
        <Tooltip title={[fit.idHidden ? idText : null, lastSessionText].filter(Boolean).join(' · ')}>
          <Typography.Title level={4} className={`${styles.name} cc-name`} data-fit-trunc="" data-testid="cc-client-header-name">
            {name}
          </Typography.Title>
        </Tooltip>
        <span className={`${styles.meta} cc-meta`}>
          <Typography.Text
            type="secondary"
            data-fit-id=""
            style={fit.idHidden ? { display: 'none' } : undefined}
            data-testid="cc-client-header-id"
          >
            {idText}
          </Typography.Text>
          <Tooltip title={fit.squeezed ? appChipText : undefined}>
            <Tag data-fit-trunc="" data-testid="cc-client-header-app-chip">
              {appChipText}
            </Tag>
          </Tooltip>
          {accessChip ? (
            <Tooltip title={fit.squeezed ? accessChip : undefined}>
              <Tag data-fit-trunc="" data-testid="cc-client-header-access-chip">
                {accessChip}
              </Tag>
            </Tooltip>
          ) : null}
          <Typography.Text
            type="secondary"
            data-fit-ls=""
            style={fit.lastSessionHidden ? { display: 'none' } : undefined}
            data-testid="cc-client-header-last-session"
          >
            {lastSessionText}
          </Typography.Text>
        </span>
      </div>
      {timerElsewhere ? (
        <Typography.Text type="secondary" className={styles.elsewhere} data-testid="cc-client-header-session-elsewhere">
          {t('components.header.sessionPinnedTo', { client: pinnedLabel })}
        </Typography.Text>
      ) : null}
      {/* the compact actions (32 px, Q-nav-2 A) — the portal's own Button tokens, only the height and padding change */}
      <ConfigProvider theme={{ components: { Button: { controlHeight: 32, paddingInline: 12, contentFontSize: 13.5 } } }}>
        <div className={styles.actions}>
          <Tooltip title={timerElsewhere ? t('components.header.sessionPinnedTo', { client: pinnedLabel }) : undefined}>
            <Button
              type={timer ? 'primary' : 'default'}
              icon={timer ? undefined : <CaretRightOutlined />}
              loading={sessionTimer?.saving}
              onClick={onSessionClick}
              data-testid="cc-client-header-session-button"
              data-session-client={timer?.clientId ?? ''}
            >
              {timer ? t('components.header.finishSession') : t('components.header.startSession')}
            </Button>
          </Tooltip>
          <ReviewSessionControl clientId={clientId} locale={locale} testIdPrefix="cc-client-header-review-session" />
          {action('assign', t('clientPage.header.assignHomework'), <PlusOutlined />, () => setAssigning(true), 'cc-client-header-assign-homework')}
          {action(
            'message',
            t('clientPage.header.message'),
            <MailOutlined />,
            () => navigate(`/messages?client=${encodeURIComponent(clientId)}`),
            'cc-client-header-message',
          )}
        </div>
      </ConfigProvider>
      <AssignHomeworkDrawer
        open={assigning}
        clients={[
          {
            subject: clientId,
            name,
            appReady: clientData?.data?.homeworkAppReady ?? undefined,
            notConnected: isNotConnected(false, accessData?.data?.canAssign),
            lng: clientData?.data?.lng,
          },
        ]}
        clientTimeZone={clientData?.data?.timezone ?? null}
        onClose={() => setAssigning(false)}
        onAssigned={() => refreshClientPage('assign')}
      />
    </header>
  );
};
